import ActionBar, { MARGIN_ACTION_BAR } from '../ActionBar/NewActionBar';
import OT from '@opentok/client';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import Show from '../Show/Show';
import VideoCallButtons from './VideoCallButtons/VideoCallButtons';
import { ACTION_BAR_HEIGHT, BUTTON_SIZE } from './VideoConfParams';
import { ParagraphMRegular } from '../../config/designSystem/typography';
import { StyleSheet, View } from 'react-native';
import { assistanceKey, assistanceStackKey, instantHelpStackKey, privateKey, tryAgainKey } from '../../constants/routeKeys';
import { captureException } from '../../utils/sentry/sentry';
import { concatPath } from '../../utils/links/concatPath';
import { defaultTimer, useTimer } from './useTimer';
import { logAmplitude } from '../../utils/analytics/analytics';
import { queryUpdateWpoRequest } from '../../state/queries/InstantHelp';
import { router } from 'expo-router';
import { useAvailableDimensions } from '../../hooks/ui/useAvailableDimensions';
import { useRingtone } from './useRingtone';
import { useStores } from '../../hooks/useStores';


const MARGIN_BOTTOM_BUTTONS = 32;
const MARGIN_BOTTOM_USER = 32;
const MARGIN_TOP_BUTTONS = 24;
const WIDTH_USER = 180;
const HEIGHT_USER = 240;
const bottomPublisher = MARGIN_BOTTOM_BUTTONS+MARGIN_TOP_BUTTONS + MARGIN_BOTTOM_USER+ BUTTON_SIZE;

const styles = StyleSheet.create({
  subscriber: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
  },
  publisher: {
    position: 'absolute',
    width: WIDTH_USER,
    height: HEIGHT_USER,
    right: 10,
    bottom: bottomPublisher,
    borderRadius: 12,
    zIndex: 100,
    flex: 1,
  },
  bottom: {
    position: 'absolute',
    bottom: MARGIN_BOTTOM_BUTTONS,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    alignSelf: 'center',
    marginHorizontal: 16,
    marginTop: MARGIN_TOP_BUTTONS,
  },
  videosContainer: {
    flex: 1,
  },
});

interface State {
  publishVideo?: boolean;
  publishAudio?: boolean;
  connected?: boolean;
  reconnecting?: boolean;
  counsellorConnected?: boolean;
  shouldExpire?: boolean;
  expired?: boolean;
  error?: any;
}
interface Session {
  openTokKey: string;
  openTokSessionId: string;
  openTokToken: string;
  setState: Dispatch<SetStateAction<State>>;
}
const initializeSession = ({
  openTokKey,
  openTokSessionId,
  openTokToken,
  setState,
}: Session) => {

  const session = OT.initSession(openTokKey, openTokSessionId);
  let subscriber: OT.Subscriber | undefined;
  const setError = (error: any) => {
    if (error) {
      captureException(error);
    }
    setState(st => ({...st, error}));
  };

  // Create a publisher
  const publisher = OT.initPublisher('publisher', {
    insertMode: 'append',
    width: '100%',
    height: '100%',
    style : {
      buttonDisplayMode:'off'
    }
  }, setError);

  // Connect to the session
  session.connect(openTokToken, (error: any) => {
    // If the connection is successful, publish to the session
    if (error) {
      setError(error);
    } else {
      session.publish(publisher, setError);
    }
  });
  const subscriberEventHandlers = {
    connected: (event?: any) => {
      logAmplitude('Instant Help Video Counsellor Joined');
      setState(state => ({...state, counsellorConnected: true}));
      console.log('someone else connected', event);
      setState(st => ({...st, shouldExpire: false}));
    },
    disconnected: (event?: any) => {
      logAmplitude('Instant Help Video Counsellor Left');
      setState(state => ({...state, counsellorConnected: false}));
      console.log('subscriber disconnected', event);
    },
    error: (event: any) => {
      logAmplitude('Instant Help Video Counsellor Error');
      setState(state => ({...state, counsellorConnected: false}));
      console.error('subscriber error', event);
      captureException(event);
    },
  };
  const sessionEventHandlers = {
    streamCreated: (event: any) => {
      subscriber = session.subscribe(event.stream, 'subscriber', {
        insertMode: 'append',
        width: '100%',
        height: '100%',
        style : {
          buttonDisplayMode:'off'
        }
      }, setError);
      for (const [type, handler] of Object.entries(subscriberEventHandlers)) {
        subscriber.on(type, handler);
      }
      setState(st => ({...st, counsellorConnected: true}));
    },
    streamDestroyed: (event: any) => {
      console.log('session.streamDestroyed');
      setState(st => ({...st, counsellorConnected: false}));
    },
    sessionReconnected: (event: any) => {
      console.log('session.sessionReconnected');
      console.log(event);
      setState(st => ({...st, reconnecting: false}));
    },
    sessionReconnecting: (event: any) => {
      console.log('session.sessionReconnecting');
      console.log(event);
      setState(st => ({...st, reconnecting: true}));
    },
    sessionDisconnected: (event: any) => {
      console.log('session.sessionDisconnected');
      console.log(event);
    },
  };

  const publisherEventHandlers = {
    streamCreated: (event: any) => {
      console.log('publisher.streamCreated');
      logAmplitude('Instant Help Video Call Started');
      setState(state => ({...state, connected: true}));
    },
    error: (event: any) => {
      console.error('Publisher error', event);
    }
  };


  // register session events
  for (const [type, handler] of Object.entries(sessionEventHandlers)) {
    session.on(type, handler);
  }
  for (const [type, handler] of Object.entries(publisherEventHandlers)) {
    publisher.on(type, handler);
  }

  // unregister event handlers
  const cleanup = () => {
    for (const [type, handler] of Object.entries(sessionEventHandlers)) {
      session.off(type, handler);
    }
    for (const [type, handler] of Object.entries(publisherEventHandlers)) {
      publisher.off(type, handler);
    }
    for (const [type, handler] of Object.entries(subscriberEventHandlers)) {
      subscriber?.off(type, handler);
    }
    publisher?.destroy();
    session?.disconnect();
  };

  return {session, publisher, cleanup, subscriber};
};


export interface InstantHelpProps {
  requestId: number;
  openTokKey: string;
  openTokSessionId: string;
  openTokToken: string;
}
export default function VideoConf(props: InstantHelpProps) {

  const rootStore = useStores();
  const {height} = useAvailableDimensions();

  const labels = {
    instantHelp: rootStore?.language.translate?.MOBILE?.ASSISTANCE?.INSTANT_HELP,
    call: rootStore?.language.translate?.MOBILE?.ASSISTANCE?.CALL,
  };
  const [publisher, setPublisher] = useState<OT.Publisher | undefined>();
  const [cleanUp, setCleanUp] = useState<() => void | undefined>();

  const [state, setState] = useState<State>({
    shouldExpire: true,
    publishAudio: true,
    publishVideo: true,
  });

  const cleanupRingtone = useRingtone(!state.counsellorConnected);

  const goToAssistance = () => {

    router.navigate(concatPath([privateKey, assistanceStackKey, assistanceKey], -1) as string);
  };

  const goToTryAgain = (reason: string) => {


    router.navigate({
      pathname: concatPath([privateKey, assistanceStackKey, instantHelpStackKey, tryAgainKey], -1) as string,
      params: {
        reason
      } });
  };

  // Call can expire if no one joins in time
  const handleExpired =  () => {
    console.log(`handleExpired shouldExpire=${state.shouldExpire}`);
    if (state.shouldExpire && !state.expired) {
      setState(st => ({...st, expired: true}));
      queryUpdateWpoRequest(
        props.requestId,
        'EXPIRED',
        'VIDEO',
      ).catch(console.error);
      goToTryAgain('counsellorTimeout');
      cleanUp?.();
      onExit();
    }

  };


  const callExpiryTimer = useTimer({countdownFrom: defaultTimer, alarmHandler: handleExpired});

  // if counsellor leaves they have a limited time to reconnect
  const handleCounsellorTimeout = () => {
    if (!state.counsellorConnected) {
      console.log('counsellor reconnect timeout');
      console.log('handle cancel 0');
      handleCancel();
      goToTryAgain('counsellorGone');
    }

  };
  const counsellorReconnectTimer = useTimer({countdownFrom: 30, alarmHandler: handleCounsellorTimeout});

  // confirm close event listener
  useEffect(() => {
    const message = labels?.call?.CONFIRM_LEAVE;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.connected) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      console.log('handle cancel 2');
      handleCancel();
      callExpiryTimer.handleTimerStop();
      counsellorReconnectTimer.handleTimerStop();
    };
  }, []);

  const onExit = () => {
    console.log('ON EXIT');
    cleanupRingtone();
  };

  const init = useCallback(() => {
    return initializeSession({
      openTokKey: props.openTokKey,
      openTokSessionId: props.openTokSessionId,
      openTokToken: props.openTokToken,
      setState,
    });}, [props.openTokKey, props.openTokSessionId, props.openTokToken]);


  useEffect(() => {
    const {session, publisher, cleanup, subscriber} = init();
    setPublisher(publisher);
    setCleanUp(() => cleanup);
    publisher?.on('streamCreated', callExpiryTimer.handleTimerStart);
    session?.on('streamDestroyed', counsellorReconnectTimer.handleTimerStart);
    subscriber?.on('connected', counsellorReconnectTimer.handleTimerStop);
    return () => {
      console.log('cleanup...');
      publisher?.off('streamCreated', callExpiryTimer.handleTimerStart);
      session?.off('streamDestroyed', counsellorReconnectTimer.handleTimerStart);
      subscriber?.off('connected', counsellorReconnectTimer.handleTimerStop);
      cleanup?.();
    };
  }, [callExpiryTimer.handleTimerStart, counsellorReconnectTimer.handleTimerStart, counsellorReconnectTimer.handleTimerStop, init]);














  useEffect(() => {
    if (state.error) {
      console.error(state.error);
    }
  }, [state.error]);






  const handleCancel = () => {
    console.log('handleCancel');
    logAmplitude('Instant Help Video Call Ended');
    cleanUp?.(); // not to be mistaken with cleanup who is only defined in a useEffect
    queryUpdateWpoRequest(
      props.requestId,
      'CANCELLED',
      'VIDEO', // TODO language?
    ).catch(console.error);
    onExit();
  };

  const toggleAudio = () => {
    setState(st => {
      publisher?.publishAudio(!st.publishAudio);
      return {...st, publishAudio: !st.publishAudio};
    });
  };

  const toggleVideo = () => {
    setState(st => {
      publisher?.publishVideo(!st.publishVideo);
      return {...st, publishVideo: !st.publishVideo};
    });
  };

  const endCall = () => {
    console.log('handle cancel 3');
    handleCancel();
    goToAssistance();
  };

  return (
    <div id="videos" style={styles.videosContainer}>

      <ActionBar
        width='100%'
        height={ACTION_BAR_HEIGHT}
        title={labels.instantHelp?.TITLE}
        onPrevious={endCall}
        hideExit={true}
      />
      <Show when={state.expired || state.reconnecting || !state.counsellorConnected}>
        <View style={{height: height - bottomPublisher - HEIGHT_USER- ACTION_BAR_HEIGHT- 2*MARGIN_ACTION_BAR, alignItems: 'center', justifyContent: 'center'}}>
          <Show when={!state.expired}>
            <Show when={state.reconnecting}>
              <ParagraphMRegular>
                {labels.call?.RECONNECTING}
              </ParagraphMRegular>
            </Show>
            <Show when={!state.counsellorConnected && !state.reconnecting}>
              <ParagraphMRegular>
                {labels.call?.WAITING}
              </ParagraphMRegular>
            </Show>
          </Show>
          <Show when={state.expired}>
            <ParagraphMRegular>
              {labels.call.EXPIRED_CALL}
            </ParagraphMRegular>
          </Show>
        </View>
      </Show>

      <div id="subscriber" style={styles.subscriber} />
      <div id="publisher" style={styles.publisher} />
      <div style={styles.bottom}>
        <VideoCallButtons
          toggleAudio={toggleAudio}
          audioOn={state.publishAudio}
          toggleVideo={toggleVideo}
          videoOn={state.publishVideo}
          endCall={endCall}
          size={BUTTON_SIZE}
        />
      </div>
    </div>
  );
}
